import React, {useState, useEffect} from 'react';
import {Box, Stack, Typography, IconButton} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {t} from 'i18next';
import './SaveSearchDrawer.scss';
import ArrButton from '../../common/arr-button/ArrButton';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import ArrRadio from '../../common/arr-radio/ArrRadio';
import ArrCheckbox from '../../common/arr-checkbox/ArrCheckbox';
import ArrTreeView from '../../common/arr-tree-view/ArrTreeView';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../shared/constants';
import {ArrDrawer} from '../../common/arr-drawer/ArrDrawer';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import {ISaveResponse} from '../../../api/types/sessionSearch.type';
import {
  useGetSavedSearchTreeData,
  useGetMySearchesTreeDetails,
} from '../../../hooks/saved-search/useSavedSearch';
import {useSaveSearch} from '../../../hooks/session-search/useSessionSearch';
import {
  convertToTree,
  pruneTree,
  hideParentCheckbox,
} from '../../../utils/formatTreeData';
import {
  hasConceptualSearchCriteria,
  createSaveRequest,
  isSavedSearchLimitExceeded,
} from '../utilities/utilities';
import {SessionSearch} from '../types/SessionSearch';
import {IIndexDropdownResponse} from '../../../api/types/searchType.type';

export interface SaveSearchDrawerProps {
  item: SessionSearch;
  isOpen: boolean;
  savedSearchLimit?: number;
  onClose?: () => void;
  onSave?: (item: SessionSearch) => void;
  selectedIndexData?: IIndexDropdownResponse;
}

const radioOptions = [
  {
    label: t('sessionSearch.saveDrawer.saveAsNewOption'),
    value: 'saveAsNewOption',
  },
  {
    label: t('sessionSearch.saveDrawer.overwriteOption'),
    value: 'overwriteOption',
  },
];

const SaveSearchDrawer: React.FC<SaveSearchDrawerProps> = ({
  item,
  isOpen,
  savedSearchLimit = -1,
  onClose,
  onSave,
  selectedIndexData,
}) => {
  const [canSave, setCanSave] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState('saveAsNewOption');
  const [retainConfidenceScore, setRetainConfidenceScore] =
    useState<boolean>(false);
  const [searchName, setSearchName] = useState<string>('');
  const [allTreeItems, setAllTreeItems] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);
  const [allSelectedTreeItems, setAllSelectedTreeItems] = useState<string[]>(
    []
  );
  const [allSelectedItem, setAllSelectedItem] = useState<string | undefined>();
  const [myTreeItems, setMyTreeItems] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);
  const [mySelectedTreeItems, setMySelectedTreeItems] = useState<string[]>([]);
  const [showErrorToast, setShowErrorToast] = useState<boolean>(false);
  const [errorToastMessage, setErrorToastMessage] = useState<string>('');

  const {
    data: allTreeData,
    isPending: allTreeDataPending,
    isError: allTreeDataErred,
  } = useGetSavedSearchTreeData();

  const {
    data: myTreeData,
    isPending: myTreeDataPending,
    isError: myTreeDataErred,
  } = useGetMySearchesTreeDetails();

  const {mutate: saveSearch} = useSaveSearch();

  useEffect(() => {
    // Overwrie - saved searches only
    let t = convertToTree(allTreeData ?? []);
    t = pruneTree(t);
    hideParentCheckbox(t);
    setAllTreeItems(t);
  }, [allTreeData]);

  useEffect(() => {
    // Save as new - groups only
    const t = convertToTree(myTreeData?.searchesTreeData ?? []);
    setMyTreeItems(t);
  }, [myTreeData]);

  useEffect(() => {
    // Reset form fields
    setSearchName('');
    setMySelectedTreeItems([]);

    if (isSaveAsNew()) {
      setAllSelectedItem(undefined);
      setAllSelectedTreeItems([]);
    } else {
      // Overwrite - set this search as the currently selected item in the tree
      const savedSearch = allTreeData?.find((i) => i.text === item.name);
      if (savedSearch) {
        setAllSelectedItem(savedSearch.id);
        setAllSelectedTreeItems([savedSearch.id]);
      } else {
        setAllSelectedItem(undefined);
        setAllSelectedTreeItems([]);
      }
    }
  }, [selectedOption]);

  useEffect(() => {
    // Validate the form
    setCanSave(false);
    if (isSaveAsNew()) {
      const hasName = searchName && searchName.length > 1;
      const hasGroupSelection =
        mySelectedTreeItems && mySelectedTreeItems.length > 0;
      if (hasName && hasGroupSelection) {
        setCanSave(true);
      }
    } else {
      const hasSearchSelection =
        allSelectedTreeItems && allSelectedTreeItems.length > 0;
      if (hasSearchSelection) {
        setCanSave(true);
      }
    }
  }, [searchName, allSelectedTreeItems, mySelectedTreeItems]);

  const isSaveAsNew = () => selectedOption === 'saveAsNewOption';

  const handleCloseClick = () => {
    onClose && onClose();
  };

  const handleCloseKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      onClose && onClose();
    }
  };

  const save = () => {
    setShowErrorToast(false);
    setErrorToastMessage('');

    if (savedSearchLimit !== -1 && isSavedSearchLimitExceeded(item, savedSearchLimit)) {
      setErrorToastMessage(
        t('sessionSearch.errors.savedSearchLimitExceeded', {
          limit: savedSearchLimit,
        })
      );
      setShowErrorToast(true);
      return;
    }

    const securityGroupId = 1;
    const currentId = item.advancedSearchId ?? 0;
    let destinationId = 0;
    let groupId = 0;
    let name = '';
    let saveSearchSecurityGroupId;

    if (isSaveAsNew()) {
      // Name = input value
      name = searchName;

      // Group ID = selected tree node
      const selectedGroupId = mySelectedTreeItems[0];
      groupId = parseInt(selectedGroupId);
      const selectSearchTree = myTreeData?.searchesTreeData?.find(
        (item) => item.id === selectedGroupId
      );
      saveSearchSecurityGroupId =
        selectedIndexData && selectSearchTree?.securitygroupsearchid;
    } else {
      // Name = selected tree node
      const savedSearchId = allSelectedTreeItems[0];
      const savedSearch = allTreeData?.find((i) => i.id === savedSearchId);
      name = savedSearch?.text ?? '';
      saveSearchSecurityGroupId =
        selectedIndexData && savedSearch?.saveSearchSecurityGroupId;
      destinationId = parseInt(savedSearch?.id ?? '0');
      const parentId = savedSearch?.parent?.replace('g', '') ?? '0';
      groupId = parseInt(parentId);
    }
    const customIndexID = selectedIndexData?.indexID ?? undefined;
    if (
      selectedIndexData?.indexID == null ||
      saveSearchSecurityGroupId === 0 ||
      (saveSearchSecurityGroupId !== undefined &&
        selectedIndexData?.securityGroupsIDs?.includes(
          saveSearchSecurityGroupId
        ))
    ) {
      const request = createSaveRequest(
        item,
        name,
        retainConfidenceScore,
        currentId,
        destinationId,
        groupId,
        securityGroupId,
        selectedIndexData?.securityGroupsIDs ?? [],
        saveSearchSecurityGroupId === 0 ? undefined : saveSearchSecurityGroupId,
        customIndexID
      );
      saveSearch(request, {
        onSuccess(data: ISaveResponse) {
          item.name = name;
          item.isSaved = true;
          item.indexId = customIndexID ?? 0;
          if (!item.advancedSearchId) {
            item.advancedSearchId = data.searchID;
          }
          onSave && onSave(item);
        },
        onError() {
          setErrorToastMessage(t('sessionSearch.saveDrawer.saveError'));
          setShowErrorToast(true);
        },
      });
    } else {
      setErrorToastMessage(
        t('sessionSearch.saveDrawer.saveErrorSecurityGroup')
      );
      setShowErrorToast(true);
    }
  };

  const handleSaveKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      save();
    }
  };

  const renderSearchNameInput = () => {
    if (isSaveAsNew()) {
      return (
        <Box className="save-name">
          <ArrTextField
            value={searchName}
            onChange={(e) => {
              setSearchName(e.target.value);
            }}
            label={t('sessionSearch.saveDrawer.searchNameLabel')}
            placeholder={t('sessionSearch.saveDrawer.searchNamePlaceholder')}
            dataTestId="save-search-drawer-name"
          ></ArrTextField>
        </Box>
      );
    } else {
      return <></>;
    }
  };

  const renderConfidenceScoreInput = () => {
    let component = <></>;

    if (hasConceptualSearchCriteria(item)) {
      const labelWithTooltip = (
        <>
          {t('sessionSearch.saveDrawer.retainConfidenceScore')}
          &nbsp;
          <ArrTooltipWrapper
            trigger={
              <IconButton
                aria-label="Info"
                sx={{padding: 0, marginBottom: '2px'}}
                className="icon-info"
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            }
          >
            {t('sessionSearch.saveDrawer.retainConfidenceScoreTooltip')}
          </ArrTooltipWrapper>
        </>
      );

      component = (
        <Box className="confidence-score">
          <ArrCheckbox
            checked={retainConfidenceScore}
            onChange={setRetainConfidenceScore}
            labelText={labelWithTooltip}
            aria-label={`retain-confidence-score-cb`}
          />
        </Box>
      );
    }

    return component;
  };

  const renderTreeHeader = () => {
    let header = t('sessionSearch.saveDrawer.selectSearchToOverwrite');
    if (isSaveAsNew()) {
      header = t('sessionSearch.saveDrawer.selectSearchGroup');
    }

    return (
      <Box className="search-groups-header">
        <Typography
          className="typo-text subhead4_size"
          data-testid={`save-search-drawer-header-name`}
          tabIndex={0}
        >
          {header}
        </Typography>
      </Box>
    );
  };

  const renderTreeView = () => {
    let component = <></>;

    if (isSaveAsNew()) {
      component = renderMyTreeView();
    } else {
      component = renderAllTreeView();
    }

    return <Box className="search-groups-content">{component}</Box>;
  };

  const renderAllTreeView = () => {
    let component = <></>;

    if (allTreeDataPending) {
      component = <ArrLoadingSpinner />;
    } else if (allTreeDataErred) {
      component = <>{t('sessionSearch.unexpectedError')}</>;
    } else {
      component = (
        <ArrTreeView
          itemsList={allTreeItems}
          selectedItem={allSelectedItem}
          setSelectedItem={(items) => {
            setAllSelectedTreeItems(items ?? []);
          }}
          handleNoResultMessage={t(
            'sessionSearch.saveDrawer.noSearchGroupsError'
          )}
          isChildrenIndendation={true}
          isShowCheckbox={true}
          multipleSelection={false}
        />
      );
    }

    return component;
  };

  const renderMyTreeView = () => {
    let component = <></>;

    if (myTreeDataPending) {
      component = <ArrLoadingSpinner />;
    } else if (myTreeDataErred) {
      component = <>{t('sessionSearch.unexpectedError')}</>;
    } else {
      component = (
        <ArrTreeView
          itemsList={myTreeItems}
          setSelectedItem={(items) => {
            setMySelectedTreeItems(items ?? []);
          }}
          handleNoResultMessage={t(
            'sessionSearch.saveDrawer.noSearchGroupsError'
          )}
          isChildrenIndendation={true}
          isShowCheckbox={true}
          multipleSelection={false}
        />
      );
    }

    return component;
  };

  return (
    <>
      <ArrDrawer
        size="large"
        side="right"
        showButton={false}
        showHeader={false}
        isOpen={isOpen}
        onClose={() => {
          onClose && onClose();
        }}
      >
        <Stack className="save-search-drawer">
          <Stack direction={'row'} spacing={1} className="drawer-header">
            <Box className="drawer-title">
              <Typography
                className="typo-text headline3_size"
                data-testid={`save-search-drawer-title`}
                tabIndex={0}
              >
                {t('sessionSearch.saveDrawer.title')}
              </Typography>
            </Box>
            <Stack direction={'row'} spacing={1} className="drawer-actions">
              <ArrButton
                size="medium"
                variant="secondary"
                onClick={handleCloseClick}
                onKeyDown={handleCloseKeyDown}
                className="drawer-button-cancel"
                dataTestId="button-cancel"
              >
                {t('sessionSearch.saveDrawer.cancelButton')}
              </ArrButton>
              <ArrButton
                size="medium"
                variant="primary"
                onClick={save}
                onKeyDown={handleSaveKeyDown}
                isAriaDisabled={!canSave}
                className={`button-insert ${canSave ? 'btn-disabled' : ''}`}
                dataTestId="drawer-button-insert"
              >
                {t('sessionSearch.saveDrawer.saveButton')}
              </ArrButton>
            </Stack>
          </Stack>

          <Box className="form-fields">
            <Box className="save-options">
              <ArrRadio
                dataTestId="session-search-save-options"
                name="session-search-save-options"
                onChange={(value) => setSelectedOption(value)}
                value={selectedOption}
                data={radioOptions}
                row={true}
              />
            </Box>

            {renderSearchNameInput()}

            {renderConfidenceScoreInput()}
          </Box>

          {renderTreeHeader()}

          {renderTreeView()}
        </Stack>
      </ArrDrawer>
      {showErrorToast && (
        <ArrToastNotification
          message={
            errorToastMessage || t('sessionSearch.saveDrawer.saveError')
          }
          onClose={() => {
            setShowErrorToast(false);
            setErrorToastMessage('');
          }}
          status={[ToastType.Error]}
        />
      )}
    </>
  );
};

export default SaveSearchDrawer;
